import { REWARDS_WIDGET_NAMES } from "@/constants/widget-names";
import { growaveConfigurationToken } from "@/tokens";
import { isShadowRoot } from "@/utils/isShadowRoot/isShadowRoot";
import { RewardsTranslationNamespaces } from "@apps/rewards/constants/constants";
import RewardsAmountFormatter from "@apps/rewards/lib/RewardsAmountFormatter/RewardsAmountFormatter";
import RewardsEarnPointsQuery from "@apps/rewards/queries/RewardsEarnPointsQuery";
import RewardsDiResolverService from "@apps/rewards/services/RewardsDiResolverService/RewardsDIResolverService";
import RewardsEarnPointsService from "@apps/rewards/services/RewardsEarnPointsService";
import RewardsStorefrontValuesService from "@apps/rewards/services/RewardsStorefrontValuesService";
import type { RewardsEarnPointsProductPageWidgetConfigInterface } from "@apps/rewards/widgets/rewards_earn_points_product_page_widget/RewardsEarnPointsProductPageWidgetConfig";
import RewardsPopUpWidgetLauncherManager from "@apps/rewards/widgets/rewards_popup_widget/utils/RewardsPopUpWidgetLauncherManager/RewardsPopUpWidgetLauncherManager";
import CustomizationManager from "@lib/CustomizationManager/CustomizationManager";
import type { ProductVariantIdManagerInterface } from "@lib/ProductVariantIdManager/ProductVariantIdManagerInterface";
import { I18nModule } from "@modules/i18n/I18nModule";
import type { SvelteComponent } from "svelte";
import { injectable } from "tsyringe";

import type { LoggerInterface } from "@interfaces/LoggerInterface";
import type { WidgetInterface } from "@interfaces/WidgetInterface";

import RewardsEarnPointsProductPageWidgetComponent from "./RewardsEarnPointsProductPageWidget.svelte";

@injectable()
class RewardsEarnPointsProductPageWidget implements WidgetInterface {
    private component: SvelteComponent | null;

    constructor(
        private readonly config: RewardsEarnPointsProductPageWidgetConfigInterface,
        private readonly logger: LoggerInterface,
        private readonly productVariantsIdManager: ProductVariantIdManagerInterface
    ) {
        this.logger.debug("RewardsEarnPointsProductPageWidget is initialized");
        this.component = null;
    }

    async init(): Promise<this> {
        const widgetNamespace =
            RewardsTranslationNamespaces.rewardsCriticalEarnPointsProductPageWidget;
        const pointsNamespace =
            RewardsTranslationNamespaces.commonCriticalPoints;

        const diResolverService = new RewardsDiResolverService();
        const {
            i18nModule,
            customizationManager,
            rewardsEarnPointsService,
            rewardsEarnPointsQuery,
            launcherManager,
            rewardsAmountFormatter,
            rewardsStorefrontValuesService,
            growaveConfiguration,
        } = diResolverService.resolveAll({
            i18nModule: I18nModule,
            customizationManager: CustomizationManager,
            rewardsEarnPointsService: RewardsEarnPointsService,
            rewardsEarnPointsQuery: RewardsEarnPointsQuery,
            launcherManager: RewardsPopUpWidgetLauncherManager,
            rewardsAmountFormatter: RewardsAmountFormatter,
            rewardsStorefrontValuesService: RewardsStorefrontValuesService,
            growaveConfiguration: growaveConfigurationToken,
        });

        const { i18n, i18nCriticalLoaderFactory } = i18nModule.exports;
        i18n.registerLoader(
            widgetNamespace,
            i18nCriticalLoaderFactory.getLoader(widgetNamespace)
        );
        i18n.registerLoader(
            pointsNamespace,
            i18nCriticalLoaderFactory.getLoader(pointsNamespace)
        );
        const pointsName$ = i18n.selectT(pointsNamespace, "points_name_plural");

        const element = this.config.target;
        const shadowRoot = isShadowRoot(
            this.config.isWidgetShadowRootEnabled,
            growaveConfiguration.isShadowRootDisabledGlobally
        )
            ? element.shadowRoot || element.attachShadow({ mode: "open" })
            : null;

        const customizationManagerConfig = {
            widgetName:
                REWARDS_WIDGET_NAMES.REWARDS_EARN_POINTS_PRODUCT_PAGE_WIDGET,
            placeholder: element,
            shadowRoot,
        };

        customizationManager.injectStyles(customizationManagerConfig);
        await customizationManager.beforeWidgetInject(
            customizationManagerConfig
        );

        this.component = new RewardsEarnPointsProductPageWidgetComponent({
            target: shadowRoot || element,
            props: {
                rewardsEarnPointsService,
                rewardsStorefrontValuesService,
                rewardsEarnPointsQuery,
                launcherManager,
                rewardsAmountFormatter,
                productId: this.config.productId,
                productVariantIdManager: this.productVariantsIdManager,
                widgetNamespace,
                pointsName$,
                afterWidgetInject: () =>
                    customizationManager.afterWidgetInject(
                        customizationManagerConfig
                    ),
            },
        });

        return this;
    }
}

export default RewardsEarnPointsProductPageWidget;
